import { getContext } from '@/context';

/**
 * Preset slug carried by a host page's path.
 *
 * Only a two-or-more segment path names a preset: gen.ai's preset URLs are
 * `/<category>/<slug>`, while `/`, `/<category>`, `/search` and `/profile` are
 * pages of its own. Without that guard every host page would be read as a
 * preset slug and resolve to nothing, dropping the user on the library.
 */
const parseSlug = (path: string): string | null => {
  const clean = path.split('?')[0].split('#')[0];
  const segments = clean.split('/').filter(Boolean);
  return segments.length > 1 ? segments[segments.length - 1] : null;
};

/**
 * The preset named by the host page's URL, e.g.
 * `/professional-profile/executive-portrait` → `executive-portrait`.
 *
 * Prefers the SDK location, which the host runtime keeps in step with its own
 * navigation; falls back to the browser path because that model can still be
 * empty on the first frame, before the host has pushed anything into it.
 */
const getPresetSlugFromHostRoute = (): string | null => {
  try {
    const { route } = getContext().general.location.getState();
    const fromRoute = route ? parseSlug(route) : null;
    if (fromRoute) return fromRoute;
  } catch {
    /* context not ready — fall back to the browser path */
  }

  return typeof window === 'undefined'
    ? null
    : parseSlug(window.location.pathname);
};

export default getPresetSlugFromHostRoute;
